import axios from "axios";
import BASE_URL from "../../config";

const getAuthHeader = () => {
  const tokenData = JSON.parse(localStorage.getItem("authUser") || "{}");
  return {
    Authorization: `Bearer ${tokenData.accessToken}`,
    'Content-Type': 'application/json'
  };
};

/**
 * Insert stock selection lock when user selects stocks
 * @param {string} stockIds - Comma-separated list of stock IDs
 * @param {string} temporaryFormUniqueId - Unique form identifier (e.g., SI-20250116-123456-001)
 * @param {number} flag - Form type flag (3=Purchase Return, 5=Transfer, 7=Sales, 8=Sales Return)
 * @param {number} createdBy - User ID
 * @returns {Promise<{isSuccess: boolean, message: string}>}
 */
export const insertStockSelectionLock = async (stockIds, temporaryFormUniqueId, flag, createdBy) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/StockSelectionLock/insert-lock`, {
      stockIds: stockIds,
      temporaryFormUniqueId: temporaryFormUniqueId,
      flag: flag,
      createdBy: createdBy
    }, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error("Error inserting stock selection lock:", error);
    throw error;
  }
};

/**
 * Delete stock selection lock(s)
 * @param {string} temporaryFormUniqueId - Form identifier to delete locks for
 * @param {string} stockIds - Optional: Comma-separated list of specific stock IDs to unlock
 * @returns {Promise<{isSuccess: boolean, message: string}>}
 */
export const deleteStockSelectionLock = async (temporaryFormUniqueId, stockIds = null) => {
  try {
    // Build request body - only include stockIds if provided
    const requestBody = {
      temporaryFormUniqueId: temporaryFormUniqueId
    };
    
    // Only add stockIds if it's provided (not null/undefined)
    if (stockIds !== null && stockIds !== undefined && stockIds !== '') {
      requestBody.stockIds = stockIds;
    } else {
      // Send empty string instead of null to satisfy backend validation
      requestBody.stockIds = '';
    }
    
    const response = await axios.post(`${BASE_URL}/api/StockSelectionLock/delete-lock`, requestBody, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error("Error deleting stock selection lock:", error);
    throw error;
  }
};

/**
 * Get locked stocks for filtering
 * @param {number} productId - Optional: Filter by product ID
 * @param {string} temporaryFormUniqueId - Exclude locks for this form ID
 * @param {number} flag - Optional: Filter by form type flag
 * @returns {Promise<{isSuccess: boolean, lockedStocks: Array}>}
 */
export const getStockSelectionLocks = async (productId = null, temporaryFormUniqueId = null, flag = null) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/StockSelectionLock/get-locks`, {
      productId: productId,
      temporaryFormUniqueId: temporaryFormUniqueId,
      flag: flag
    }, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error("Error getting stock selection locks:", error);
    throw error;
  }
};

/**
 * Check if specific stocks are locked before save
 * @param {string} stockIds - Comma-separated list of stock IDs to check
 * @param {string} temporaryFormUniqueId - Exclude locks for this form ID
 * @returns {Promise<{isSuccess: boolean, message: string, lockedStocks: Array, hasLocks: boolean}>}
 */
export const checkStockSelectionLocks = async (stockIds, temporaryFormUniqueId) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/StockSelectionLock/check-locks`, {
      stockIds: stockIds,
      temporaryFormUniqueId: temporaryFormUniqueId
    }, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error("Error checking stock selection locks:", error);
    throw error;
  }
};

/**
 * Cleanup old stock selection locks
 * @param {number} hoursOld - Delete locks older than this many hours (default: 2)
 * @param {string} temporaryFormUniqueId - Optional: Cleanup specific form ID
 * @returns {Promise<{isSuccess: boolean, message: string}>}
 */
export const cleanupStockSelectionLocks = async (hoursOld = 2, temporaryFormUniqueId = null) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/StockSelectionLock/cleanup-locks`, {
      hoursOld: hoursOld,
      temporaryFormUniqueId: temporaryFormUniqueId
    }, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error("Error cleaning up stock selection locks:", error);
    throw error;
  }
};

/**
 * Get grouped stock selection locks (for management page)
 * @param {number} pageNumber - Page number (default: 1)
 * @param {number} pageSize - Number of records per page (default: 10)
 * @returns {Promise<{isSuccess: boolean, message: string, locks: Array, totalRecords: number, pageNumber: number, pageSize: number, totalPages: number}>}
 */
export const getStockSelectionLocksGrouped = async (pageNumber = 1, pageSize = 10) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/StockSelectionLock/get-locks-grouped`, {
      pageNumber: pageNumber,
      pageSize: pageSize
    }, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error("Error getting grouped stock selection locks:", error);
    throw error;
  }
};

/**
 * Get lock details for a specific form (products and batch numbers)
 * @param {string} temporaryFormUniqueId - Form ID to get details for
 * @returns {Promise<{isSuccess: boolean, message: string, details: Array}>}
 */
export const getStockSelectionLockDetails = async (temporaryFormUniqueId) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/StockSelectionLock/get-lock-details`, {
      temporaryFormUniqueId: temporaryFormUniqueId
    }, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error("Error getting stock selection lock details:", error);
    throw error;
  }
};

/**
 * Delete all stock selection locks
 * @returns {Promise<{isSuccess: boolean, message: string, deletedCount: number}>}
 */
export const deleteAllStockSelectionLocks = async () => {
  try {
    const response = await axios.post(`${BASE_URL}/api/StockSelectionLock/delete-all-locks`, {}, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error("Error deleting all stock selection locks:", error);
    throw error;
  }
};


